import React, { useState } from 'react';

const StudentNotes = ({ notes, loading }) => {
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const notesPerPage = 10;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading notes...</div>
      </div>
    );
  }

  if (!notes || notes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <svg className="h-12 w-12 mb-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div className="text-center">
          <p className="text-sm font-medium">No notes available</p>
          <p className="text-xs text-slate-400 mt-1">Notes from teachers will appear here</p>
        </div>
      </div>
    );
  }

  // Show only 4 most recent notes in the main view
  const displayedNotes = notes.slice(0, 4);
  const hasMoreNotes = notes.length > 4;

  // Pagination logic for overlay
  const totalPages = Math.ceil(notes.length / notesPerPage);
  const startIndex = (currentPage - 1) * notesPerPage;
  const paginatedNotes = notes.slice(startIndex, startIndex + notesPerPage);

  const NoteCard = ({ note, index }) => (
    <div
      key={note.id || index}
      className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:bg-slate-100 transition-colors"
    >
      {/* Header with date and class info */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-slate-900">
              {note.classroom?.name || note.classroom || 'Unknown Class'}
            </span>
            {note.period && (
              <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border">
                {note.period.name || `Period ${note.period.id}`}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            {new Date(note.date || note.created_at).toLocaleDateString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
            {note.period?.startTime && note.period?.endTime && (
              <span className="ml-2">
                • {note.period.startTime} - {note.period.endTime}
              </span>
            )}
          </div>
        </div>
        
        {/* Attendance status if available */}
        {note.status && (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${
            note.status.toLowerCase() === 'present' ? 'bg-green-100 text-green-800' :
            note.status.toLowerCase() === 'absent' ? 'bg-red-100 text-red-800' :
            note.status.toLowerCase() === 'late' ? 'bg-yellow-100 text-yellow-800' :
            note.status.toLowerCase() === 'excused' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {note.status}
          </span>
        )}
      </div>

      {/* Note content */}
      <div className="text-sm text-slate-700 leading-relaxed">
        {note.notes || note.content || note.text || 'No note content'}
      </div>

      {/* Additional metadata */}
      {(note.instructor || note.teacher) && (
        <div className="mt-2 pt-2 border-t border-slate-200">
          <span className="text-xs text-slate-500">
            By: {note.instructor || note.teacher}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="space-y-4">
        {displayedNotes.map((note, index) => (
          <NoteCard key={note.id || index} note={note} index={index} />
        ))}
        
        {hasMoreNotes && (
          <div className="text-center pt-4">
            <button
              onClick={() => setShowAllNotes(true)}
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors font-medium"
            >
              View All Notes ({notes.length}) →
            </button>
          </div>
        )}
        
        {!hasMoreNotes && notes.length === 4 && (
          <div className="text-center text-xs text-slate-500 pt-2">
            Showing all {notes.length} notes
          </div>
        )}
      </div>

      {/* All Notes Overlay */}
      {showAllNotes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">
                All Student Notes ({notes.length})
              </h2>
              <button
                onClick={() => {
                  setShowAllNotes(false);
                  setCurrentPage(1);
                }}
                className="text-slate-400 hover:text-slate-600 text-2xl"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="space-y-4">
                {paginatedNotes.map((note, index) => (
                  <NoteCard key={note.id || (startIndex + index)} note={note} index={startIndex + index} />
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-t border-slate-200">
                <div className="text-sm text-slate-700">
                  Showing {startIndex + 1} to {Math.min(startIndex + notesPerPage, notes.length)} of {notes.length} notes
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      currentPage === 1
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        return (
                          page === 1 ||
                          page === totalPages ||
                          Math.abs(page - currentPage) <= 1
                        );
                      })
                      .map((page, index, array) => {
                        const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                        
                        return (
                          <React.Fragment key={page}>
                            {showEllipsisBefore && (
                              <span className="px-2 py-1 text-slate-400">...</span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                page === currentPage
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      currentPage === totalPages
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StudentNotes;
